'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import * as React from 'react';
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { disableNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview';
import { useStore } from '@base-ui/utils/store';
import { useSchedulerStoreContext } from "../../use-scheduler-store-context/index.mjs";
import { schedulerEventSelectors, schedulerOccurrencePlaceholderSelectors } from "../../scheduler-selectors/index.mjs";
import { useDragPreview } from "./useDragPreview.mjs";
import { useEvent } from "./useEvent.mjs";
import { useAdapterContext } from "../../use-adapter-context/index.mjs";
export function useDraggableEvent(parameters) {
  const {
    ref,
    start,
    end,
    occurrenceKey,
    eventId,
    renderDragPreview,
    getDragData,
    collectionStart,
    collectionEnd,
    isDraggable = false
  } = parameters;

  // Context hooks
  const adapter = useAdapterContext();
  const store = useSchedulerStoreContext();

  // Selector hooks
  const placeholderAction = useStore(store, schedulerOccurrencePlaceholderSelectors.actionForOccurrence, occurrenceKey);
  const event = useStore(store, schedulerEventSelectors.processedEvent, eventId);

  // Feature hooks
  const {
    state: eventState
  } = useEvent({
    start,
    end,
    eventId
  });
  const preview = useDragPreview({
    type: 'internal-event',
    data: event,
    renderDragPreview,
    showPreviewOnDragStart: false
  });
  const state = React.useMemo(() => _extends({}, eventState, {
    dragging: placeholderAction === 'internal-drag',
    resizing: placeholderAction === 'internal-resize'
  }), [eventState, placeholderAction]);
  React.useEffect(() => {
    if (!isDraggable || !ref.current) {
      return;
    }

    // eslint-disable-next-line consistent-return
    return draggable({
      element: ref.current,
      getInitialData: ({
        input
      }) => getDragData(input),
      onGenerateDragPreview: ({
        nativeSetDragImage
      }) => {
        disableNativeDragPreview({
          nativeSetDragImage
        });
      },
      onDragStart: ({
        location
      }) => {
        preview.actions.onDragStart(location);
      },
      onDrag: ({
        location
      }) => {
        preview.actions.onDrag(location);
      },
      onDrop: () => {
        store.setOccurrencePlaceholder(null);
        preview.actions.onDrop();
      }
    });
  }, [ref, getDragData, isDraggable, store, preview.actions]);
  const contextValue = React.useMemo(() => ({
    doesEventStartBeforeCollectionStart: adapter.isBefore(start.value, collectionStart),
    doesEventEndAfterCollectionEnd: adapter.isAfter(end.value, collectionEnd)
  }), [adapter, start, end, collectionStart, collectionEnd]);
  return {
    state,
    preview,
    contextValue
  };
}